import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { registrarVisita, heartbeat, aplicarOptOut, donoNaoContar } from "../services/api";
import { useAuth } from "../context/AuthContext";

// Atualiza o <title> a cada navegação (acessibilidade + UX de abas/histórico + SEO leve).
// SPA tinha um título único para todas as rotas.
const BASE = "eaiRH — Reputação em processos seletivos";

const TITULOS = {
  "/": BASE,
  "/empresas": "Empresas — eaiRH",
  "/vagas": "Vagas — eaiRH",
  "/vagas/nova": "Publicar vaga — eaiRH",
  "/indicadores": "Ranking e indicadores de empresas em processos seletivos — eaiRH",
  "/selo": "Selo eaiRH — certificação de processos seletivos",
  "/como-funciona": "Como funciona — eaiRH",
  "/por-que": "Por que registrar — eaiRH",
  "/denunciar": "Fazer um relato — eaiRH",
  "/entrar": "Entrar — eaiRH",
  "/esqueci-senha": "Recuperar senha — eaiRH",
  "/redefinir-senha": "Redefinir senha — eaiRH",
  "/confirmar-email": "Confirmar e-mail — eaiRH",
  "/conta": "Minha conta — eaiRH",
  "/minhas-reclamacoes": "Meus relatos — eaiRH",
  "/curriculo": "Meu currículo — eaiRH",
  "/painel": "Painel da empresa — eaiRH",
  "/termos": "Termos de Uso — eaiRH",
  "/privacidade": "Política de Privacidade — eaiRH",
  "/admin": "Painel do administrador — eaiRH",
};

export default function TitleManager() {
  const { pathname, search } = useLocation();
  const [dono, setDono] = useState(() => donoNaoContar());
  const { user, impersonarEmpresa, impersonarCandidato, logout } = useAuth();
  const impersonando = user?.tipo === 1 && user?.ehAdmin === true;      // admin operando como empresa
  // Admin operando como CANDIDATO. A marca é explícita porque a própria conta do admin é do tipo candidato —
  // deduzir pelo tipo fazia o pill aparecer na sessão normal dele.
  const comoCandidato = !!user && typeof localStorage !== "undefined" && !!localStorage.getItem("eairh_imp_cand");

  // Opt-out do dono: ?eairh_naocontar=1 liga, ?eairh_contar=1 desliga. Declarado ANTES do registro de rota pra que,
  // na carga de ativação, o localStorage já esteja marcado quando o beacon (efeito abaixo) decidir não disparar.
  useEffect(() => { setDono(aplicarOptOut(search)); }, [search]);

  // Reage na hora ao botão "modo dono" (mesma aba) e a mudanças em outras abas — atualiza o selo sem precisar navegar.
  useEffect(() => {
    const sync = () => setDono(donoNaoContar());
    window.addEventListener("eairh-optout", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("eairh-optout", sync); window.removeEventListener("storage", sync); };
  }, []);

  useEffect(() => {
    let titulo = TITULOS[pathname];
    if (!titulo) {
      if (pathname.startsWith("/empresas/")) titulo = "Empresa — eaiRH";
      else if (pathname.startsWith("/vagas/")) titulo = "Vaga — eaiRH";
    }
    document.title = titulo || BASE;
    registrarVisita(pathname); // funil: registra a rota vista (cobre navegação SPA, não só a carga inicial)
  }, [pathname]);

  // Heartbeat de sessão: a cada 20s SÓ quando a aba está visível (pausa em aba de fundo) — alimenta
  // "visitas ativas" e "tempo de visita". Dispara também ao voltar o foco. Monta uma vez (app-global).
  useEffect(() => {
    const bater = () => { if (document.visibilityState === "visible") heartbeat(); };
    bater(); // batida inicial
    const t = setInterval(bater, 20000);
    document.addEventListener("visibilitychange", bater);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", bater); };
  }, []);

  // Pills fixos no canto (estilo "modo dono"): impersonação de empresa (admin) + opt-out de visitas.
  if (!impersonando && !comoCandidato && !dono) return null;
  return (
    <div className="fixed bottom-3 left-3 z-50 flex flex-col gap-2 items-start">
      {impersonando && (
        <button onClick={() => impersonarEmpresa(null)} title="Voltar a ser admin"
          className="rounded-full bg-purple-700 hover:bg-purple-800 text-white text-[11px] font-semibold px-3 py-1.5 shadow-lg">
          🎭 operando como empresa — voltar a ser admin
        </button>
      )}
      {comoCandidato && (
        <button onClick={async () => {
            // Token emitido ANTES da correção não sabe quem é o admin por trás — o servidor devolveria o
            // candidato de novo e o botão pareceria "não fazer nada". Nesse caso a saída honesta é deslogar:
            // o admin entra de novo e volta ao normal, em vez de ficar preso na conta de outra pessoa.
            try {
              await impersonarCandidato(null);
              window.location.href = "/admin";
            } catch { logout(); window.location.href = "/entrar"; }
          }} title="Voltar a ser admin"
          className="rounded-full bg-purple-700 hover:bg-purple-800 text-white text-[11px] font-semibold px-3 py-1.5 shadow-lg">
          🎭 operando como candidato — voltar a ser admin
        </button>
      )}
      {dono && (
        <div className="rounded-full bg-gray-900/85 text-white text-[11px] font-semibold px-3 py-1.5 shadow-lg select-none">
          🔇 modo dono — não conto suas visitas
        </div>
      )}
    </div>
  );
}
